<template>
  <div class="crypto-scanner" :class="{ dark: isDark, 'scanner-compact':compact }">
    <div class="scanner-header">
      <div>
        <h2>{{ title }}</h2>
        <p>{{ statusText }}</p>
      </div>
      <button class="scanner-icon-btn" type="button" @click="$emit('close')" title="Close">
        <span>&times;</span>
      </button>
    </div>

    <div class="scanner-controls">
      <div v-if="isCryptoMode" class="scanner-tabs">
        <button
          v-for="preset in presets"
          :key="preset.key"
          type="button"
          :class="{ active: activePreset === preset.key }"
          @click="setPreset(preset.key)"
        >{{ preset.label }}</button>
      </div>

      <div class="scanner-filters">
        <select v-if="isCryptoMode" v-model="market" @change="fetchScanner">
          <option value="all">Spot + Futures</option>
          <option value="spot">Spot</option>
          <option value="futures">Futures</option>
        </select>
        <select v-if="isCryptoMode" v-model="quote" @change="fetchScanner">
          <option value="">All quotes</option>
          <option value="USDT">USDT</option>
          <option value="USDC">USDC</option>
          <option value="BTC">BTC</option>
          <option value="ETH">ETH</option>
        </select>
        <select v-if="isIndianMode" v-model="indianSegment" @change="fetchScanner">
          <option value="all">All segments</option>
          <option value="indices">Indices</option>
          <option value="stocks">Stocks</option>
          <option value="futures">Futures</option>
          <option value="options">Options</option>
        </select>
        <input v-model.trim="search" type="search" :placeholder="isIndianMode ? 'Search NSE symbols' : 'Search'" @input="debouncedFetch" />
      </div>
    </div>

    <div class="scanner-table-wrap">
      <table class="scanner-table">
        <thead>
          <tr>
            <th>Instrument</th>
            <th v-if="isCryptoMode" class="num">Price</th>
            <th v-if="isCryptoMode" class="num">24h</th>
            <th v-if="isCryptoMode" class="num">Vol USD</th>
            <th v-if="isCryptoMode && !compact" class="num">VT Signal</th>
            <th v-if="isCryptoMode && !compact" class="num">VT Laser</th>
            <th v-if="isIndianMode">Segment</th>
            <th v-if="isIndianMode">Instrument Key</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="tableColspan" class="scanner-state">Loading scanner...</td>
          </tr>
          <tr v-else-if="instruments.length === 0">
            <td :colspan="tableColspan" class="scanner-state">No instruments match this scanner.</td>
          </tr>
          <tr
            v-for="item in instruments"
            :key="item.id"
            class="scanner-row"
            @click="selectInstrument(item)"
          >
            <td>
              <div class="instrument-cell">
                <span class="rank">{{ item.rank }}</span>
                <div>
                  <strong>{{ item.displaySymbol }}</strong>
                  <small>{{ item.name }} · {{ item.market }}</small>
                </div>
              </div>
              <div v-if="!compact" class="tag-row">
                <span v-for="tag in item.tags.slice(0, 3)" :key="tag">{{ formatTag(tag) }}</span>
              </div>
            </td>
            <td v-if="isCryptoMode" class="num">{{ formatPrice(item.price) }}</td>
            <td v-if="isCryptoMode" class="num" :class="changeClass(item.priceChangePercent)">
              {{ formatPct(item.priceChangePercent) }}
            </td>
            <td v-if="isCryptoMode" class="num">{{ formatCompact(item.quoteVolume) }}</td>
            <td v-if="isCryptoMode && !compact" class="num">
              <div class="vt-signal-cell">
                <span v-if="item.vtSignal" :class="vtSignalClass(item.vtSignal.signal)">
                  {{ formatScore(item.vtSignal.score) }}
                </span>
                <span v-else>-</span>
                <small v-if="item.vtSignal && item.vtSignal.signal !== 'neutral'">
                  {{ item.vtSignal.signal }}
                </small>
              </div>
            </td>
            <td v-if="isCryptoMode && !compact" class="num">
              <div class="vt-laser-cell">
                <span v-if="item.vtLaser && item.vtLaser.score > 0" :class="vtLaserClass(item.vtLaser.confidence)">
                  {{ formatScore(item.vtLaser.score) }}
                </span>
                <span v-else>-</span>
                <small v-if="item.vtLaser && item.vtLaser.setup !== 'none'">
                  {{ item.vtLaser.setup }}
                </small>
              </div>
            </td>
            <td v-if="isIndianMode">{{ item.segmentLabel }}</td>
            <td v-if="isIndianMode"><small>{{ item.instrument_key }}</small></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import IndianMarketService from '../services/indian_market_service';

export default {
  name: 'CryptoScanner',
  props: {
    isDark: { type: Boolean, default: false },
    mode: { type: String, default: 'crypto' },
    compact: { type:Boolean, default:false }
  },
  data() {
    return {
      activePreset: 'top',
      market: 'all',
      quote: 'USDT',
      indianSegment: 'all',
      search: '',
      loading: false,
      instruments: [],
      status: null,
      timer: null,
      searchTimer: null,
      presets: [
        { key: 'top', label: 'Top' },
        { key: 'volume', label: 'Volume' },
        { key: 'volatility', label: 'Volatility' },
        { key: 'movers', label: 'Sudden movers' },
        { key: 'momentum', label: 'Momentum' },
        { key: 'buildup', label: 'Volume buildup' },
        { key: 'gainers', label: 'Gainers' },
        { key: 'losers', label: 'Losers' },
        { key: 'vt-signal', label: 'VT Signals' },
        { key: 'vt-laser', label: 'VT Laser' },
        { key: 'reversal', label: 'Reversals' },
        { key: 'bullish', label: 'Bullish' },
        { key: 'bearish', label: 'Bearish' }
      ]
    };
  },
  computed: {
    isIndianMode() {
      return this.mode === 'indian';
    },
    isCryptoMode() {
      return !this.isIndianMode;
    },
    title() {
      return this.isIndianMode ? 'Indian Market Scanner' : 'Scanner';
    },
    tableColspan() {
      return this.isIndianMode ? 3 : this.compact ? 4 : 6;
    },
    statusText() {
      if (this.isIndianMode) {
        return `${this.instruments.length || 0} instruments · NSE/BSE master search`;
      }
      if (!this.status) return 'Preparing live market scan';
      const ws = [this.status.spotWs, this.status.futuresWs].filter(Boolean).join(' / ');
      return `${this.status.total || 0} instruments · ${ws || 'REST sync'}`;
    }
  },
  mounted() {
    this.fetchScanner();
    this.timer = setInterval(this.fetchScanner, this.isIndianMode ? 30000 : 10000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearTimeout(this.searchTimer);
  },
  watch: {
    mode() {
      clearInterval(this.timer);
      clearTimeout(this.searchTimer);
      this.instruments = [];
      this.status = null;
      this.fetchScanner();
      this.timer = setInterval(this.fetchScanner, this.isIndianMode ? 30000 : 10000);
    }
  },
  methods: {
    setPreset(key) {
      this.activePreset = key;
      this.fetchScanner();
    },
    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.fetchScanner, 250);
    },
    async fetchScanner() {
      this.loading = this.instruments.length === 0;
      if (this.isIndianMode) {
        await this.fetchIndianScanner();
        return;
      }

      const params = new URLSearchParams({
        preset: this.activePreset,
        market: this.market,
        quote: this.quote,
        search: this.search,
        limit: '120'
      });
      try {
        const base = process.env.VUE_APP_API_BASE || window.location.origin;
        const response = await fetch(`${base}/api/crypto-scanner?${params.toString()}`);
        const data = await response.json();
        if (data.success) {
          this.instruments = data.instruments || [];
          this.status = data.status || null;
        }
      } catch (error) {
        console.warn('[CryptoScanner] fetch failed:', error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchIndianScanner() {
      try {
        const query = this.search || this.defaultIndianQuery();
        const results = await IndianMarketService.searchSymbols(query, 120);
        const filtered = results.filter(item => {
          if (this.indianSegment === 'all') return true;
          return this.getIndianSegmentKey(item) === this.indianSegment;
        });

        this.instruments = filtered.map((item, index) => ({
          ...item,
          id: item.instrument_key || `${item.symbol}-${index}`,
          rank: index + 1,
          displaySymbol: this.cleanIndianSymbol(item.trading_symbol || item.symbol || ''),
          name: item.formatted_ins_name || item.name || item.trading_symbol || item.symbol || '',
          market: item.exchange || item.exch || 'NSE',
          tags: [this.getIndianSegmentKey(item), item.instrument_type, item.exchange].filter(Boolean),
          segmentLabel: this.getIndianSegmentLabel(item)
        }));
        this.status = null;
      } catch (error) {
        console.warn('[IndianMarketScanner] fetch failed:', error.message);
      } finally {
        this.loading = false;
      }
    },
    defaultIndianQuery() {
      if (this.indianSegment === 'indices') return 'NIFTY';
      if (this.indianSegment === 'futures') return 'NIFTY';
      if (this.indianSegment === 'options') return 'NIFTY';
      return 'RELIANCE';
    },
    cleanIndianSymbol(symbol) {
      return String(symbol || '').replace(/-EQ$/, '');
    },
    getIndianSegmentKey(item) {
      const segment = String(item.segment || item.exchange_segment || '').toUpperCase();
      const instrumentType = String(item.instrument_type || '').toUpperCase();
      const symbol = String(item.trading_symbol || item.symbol || '').toUpperCase();

      if (segment.includes('INDEX') || instrumentType.includes('INDEX')) return 'indices';
      if (instrumentType.includes('OPT') || instrumentType === 'CE' || instrumentType === 'PE' || symbol.includes(' CE') || symbol.includes(' PE')) return 'options';
      if (instrumentType.includes('FUT') || symbol.includes(' FUT')) return 'futures';
      return 'stocks';
    },
    getIndianSegmentLabel(item) {
      const key = this.getIndianSegmentKey(item);
      if (key === 'indices') return 'Indices';
      if (key === 'options') return 'Options';
      if (key === 'futures') return 'Futures';
      return item.exchange || item.exch || 'Stocks';
    },
    selectInstrument(item) {
      if (this.isIndianMode) {
        this.$emit('select', {
          symbol: item.symbol || item.trading_symbol,
          trading_symbol: item.trading_symbol || item.symbol,
          exchange: item.exchange || item.exch || 'NSE',
          exch: item.exch || item.exchange || 'NSE',
          type: 'IndianMarket',
          formatted_ins_name: item.formatted_ins_name || item.name || item.symbol,
          name: item.name || item.formatted_ins_name || item.symbol,
          instrument_key: item.instrument_key,
          segment: item.segment,
          instrument_type: item.instrument_type,
          scanner: item
        });
        return;
      }

      this.$emit('select', {
        symbol: item.symbol,
        exchange: 'Binance',
        type: item.type,
        contractType: item.market === 'futures' ? 'PERPETUAL' : item.contractType,
        baseAsset: item.baseAsset,
        quoteAsset: item.quoteAsset,
        name: item.name,
        scanner: item
      });
    },
    formatPrice(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '-';
      if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
      if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
      return n.toLocaleString(undefined, { maximumSignificantDigits: 5 });
    },
    formatPct(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '-';
      return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
    },
    formatCompact(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '-';
      return Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 }).format(n);
    },
    formatScore(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '-';
      return n.toFixed(2);
    },
    formatTag(tag) {
      return String(tag).replace(/-/g, ' ');
    },
    changeClass(value) {
      const n = Number(value);
      return {
        positive: n > 0,
        negative: n < 0
      };
    },
    vtSignalClass(signal) {
      return {
        'vt-bullish': signal === 'bullish' || signal === 'bullish_reversal',
        'vt-bearish': signal === 'bearish' || signal === 'bearish_reversal',
        'vt-neutral': signal === 'neutral' || signal === 'none'
      };
    },
    vtLaserClass(confidence) {
      const conf = Number(confidence);
      if (conf >= 80) return 'vt-laser-high';
      if (conf >= 60) return 'vt-laser-medium';
      if (conf >= 40) return 'vt-laser-low';
      return 'vt-laser-none';
    }
  }
};
</script>

<style scoped>
.crypto-scanner {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: #17202a;
  border-left: 1px solid #e3e7ee;
}

.crypto-scanner.dark {
  background: #0f1117;
  color: #eef1f6;
  border-left-color: #272b35;
}

.scanner-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px 10px;
}

.scanner-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.scanner-header p {
  margin: 4px 0 0;
  color: #7a8494;
  font-size: 12px;
}

.scanner-icon-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d9dee7;
  background: transparent;
  color: inherit;
  border-radius: 8px;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
}

.dark .scanner-icon-btn {
  border-color: #343947;
}

.scanner-controls {
  padding: 0 18px 12px;
  border-bottom: 1px solid #e6e9ef;
}

.dark .scanner-controls {
  border-bottom-color: #252a35;
}

.scanner-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 10px;
  scrollbar-width: thin;
  scrollbar-color: #dce1ea transparent;
}

.scanner-tabs::-webkit-scrollbar {
  height: 6px;
}

.scanner-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.scanner-tabs::-webkit-scrollbar-thumb {
  background-color: #dce1ea;
  border-radius: 3px;
}

.dark .scanner-tabs::-webkit-scrollbar-thumb {
  background-color: #343947;
}

.scanner-tabs button {
  border: 1px solid #dce1ea;
  background: #f7f9fc;
  color: #4f5968;
  border-radius: 8px;
  padding: 7px 10px;
  white-space: nowrap;
  cursor: pointer;
  font-size: 12px;
}

.scanner-tabs button.active {
  background: #1769ff;
  border-color: #1769ff;
  color: #ffffff;
}

.dark .scanner-tabs button {
  background: #171b24;
  border-color: #303646;
  color: #b5bfce;
}

.scanner-filters {
  display: grid;
  grid-template-columns: 1fr 0.8fr 1fr;
  gap: 8px;
}

.scanner-filters select,
.scanner-filters input {
  min-width: 0;
  border: 1px solid #dce1ea;
  border-radius: 8px;
  padding: 8px 10px;
  background: #ffffff;
  color: inherit;
  outline: none;
}

.dark .scanner-filters select,
.dark .scanner-filters input {
  background: #171b24;
  border-color: #303646;
  color: #eef1f6;
}

.scanner-table-wrap {
  overflow: auto;
  flex: 1;
  height: 0; /* Force flex child to respect parent height */
  min-height: 0;
}

.scanner-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.scanner-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  color: #7a8494;
  background: #ffffff;
  border-bottom: 1px solid #e6e9ef;
  padding: 10px 12px;
  font-weight: 600;
}

.dark .scanner-table th {
  background: #0f1117;
  border-bottom-color: #252a35;
}

.scanner-table td {
  border-bottom: 1px solid #eef1f5;
  padding: 10px 12px;
  vertical-align: top;
}

.dark .scanner-table td {
  border-bottom-color: #20242e;
}

.scanner-row {
  cursor: pointer;
}

.scanner-row:hover {
  background: #f4f7fb;
}

.dark .scanner-row:hover {
  background: #181d27;
}

.num {
  text-align: right;
  white-space: nowrap;
}

.positive {
  color: #00a884;
}

.negative {
  color: #f05266;
}

.instrument-cell {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rank {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #edf2f8;
  color: #627084;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex: 0 0 auto;
}

.dark .rank {
  background: #202633;
  color: #aab5c6;
}

.instrument-cell strong {
  display: block;
  font-size: 13px;
}

.instrument-cell small {
  display: block;
  color: #7a8494;
  margin-top: 2px;
  text-transform: capitalize;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 7px 0 0 34px;
}

.tag-row span {
  border-radius: 6px;
  background: #edf6ff;
  color: #1769ff;
  font-size: 10px;
  padding: 3px 6px;
}

.dark .tag-row span {
  background: #17243b;
  color: #74a5ff;
}

.scanner-state {
  text-align: center;
  color: #7a8494;
  padding: 28px 12px;
}

/* VT Signal and VT Laser Styles */
.vt-signal-cell,
.vt-laser-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.vt-signal-cell span,
.vt-laser-cell span {
  font-weight: 600;
  font-size: 12px;
}

.vt-signal-cell small,
.vt-laser-cell small {
  font-size: 9px;
  opacity: 0.8;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}

/* VT Signal Colors */
.vt-bullish {
  color: #00a884 !important;
}

.vt-bearish {
  color: #f05266 !important;
}

.vt-neutral {
  color: #7a8494 !important;
}

/* VT Laser Confidence Colors */
.vt-laser-high {
  color: #22c55e !important;
  background: rgba(34, 197, 94, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
}

.vt-laser-medium {
  color: #f59e0b !important;
  background: rgba(245, 158, 11, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
}

.vt-laser-low {
  color: #3b82f6 !important;
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
}

.vt-laser-none {
  color: #7a8494 !important;
}

/* Dark theme adjustments */
.dark .vt-laser-high {
  background: rgba(34, 197, 94, 0.2);
}

.dark .vt-laser-medium {
  background: rgba(245, 158, 11, 0.2);
}

.dark .vt-laser-low {
  background: rgba(59, 130, 246, 0.2);
}
</style>
